import { FC, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronDownIcon } from "@heroicons/react/24/outline";

export interface CategoryGroup {
  id: string | number;
  title: string;
  items: { id: string | number; name: string; href: string }[];
}

export interface ProductsCategorySidebarProps {
  categories: CategoryGroup[];
  className?: string;
}

const ProductsCategorySidebar: FC<ProductsCategorySidebarProps> = ({
  categories,
  className = "",
}) => {
  const { id: activeSubId } = useParams();
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  const toggleGroup = (groupId: string | number) => {
    const key = String(groupId);
    setOpenGroups((prev) => ({
      ...prev,
      [key]: !(prev[key] ?? true),
    }));
  };

  return (
    <aside className={`${className}`}>
      <div className="rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 overflow-hidden shadow-sm">
        <div className="px-5 py-4 border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/80 dark:bg-neutral-800/40">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400">
            Filter by
          </p>
          <h3 className="mt-1 text-base font-semibold text-neutral-900 dark:text-white">
            Product Range
          </h3>
        </div>

        <nav className="p-3 max-h-[calc(100vh-220px)] overflow-y-auto bcr-scrollbar-hide">
          <Link
            to="/products"
            className={`block px-3 py-2.5 rounded-xl text-sm font-medium transition-colors mb-1 ${
              !activeSubId
                ? "bg-primary-50 dark:bg-primary-900/25 text-primary-700 dark:text-primary-300"
                : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800"
            }`}
          >
            All Products
          </Link>

          {categories.map((group) => {
            const groupKey = String(group.id);
            const isOpen = openGroups[groupKey] ?? true;
            const hasActiveChild = group.items.some(
              (sub) => String(sub.id) === String(activeSubId)
            );

            return (
              <div key={group.id} className="mt-1">
                <button
                  type="button"
                  onClick={() => toggleGroup(group.id)}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl text-left text-sm font-semibold transition-colors ${
                    hasActiveChild
                      ? "text-primary-700 dark:text-primary-300"
                      : "text-neutral-800 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                  }`}
                >
                  <span className="line-clamp-1">{group.title}</span>
                  <ChevronDownIcon
                    className={`w-4 h-4 shrink-0 transition-transform ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {isOpen && (
                  <ul className="mt-0.5 ml-2 pl-2 border-l border-neutral-200 dark:border-neutral-700 space-y-0.5">
                    {group.items.map((sub) => {
                      const isActive = String(sub.id) === String(activeSubId);
                      return (
                        <li key={sub.id}>
                          <Link
                            to={sub.href}
                            className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
                              isActive
                                ? "bg-primary-50 dark:bg-primary-900/25 text-primary-700 dark:text-primary-300 font-medium"
                                : "text-neutral-600 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-300 hover:bg-neutral-50 dark:hover:bg-neutral-800/60"
                            }`}
                          >
                            {sub.name}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </nav>
      </div>
    </aside>
  );
};

export default ProductsCategorySidebar;
